import type {
  SubscriptionStatusPrimaryReason,
  SubscriptionStatusSecondaryReason,
} from "@/primary-models";

/**
 * Which secondary cancellation reasons are valid for a given primary
 * cancellation reason. Primaries not present here (e.g. COMING_BACK,
 * LIMITED_FREEZER_SPACE, TOO_EXPENSIVE, PASSED_AWAY) have no secondary
 * reason.
 *
 * Source of truth ported from
 * apps/website/hooks/customer/cancellation/useCustomerCancellationOptions.tsx
 * — keep in sync with that flow's taxonomy.
 */
export const CANCELLATION_REASON_TAXONOMY: Partial<
  Record<SubscriptionStatusPrimaryReason, SubscriptionStatusSecondaryReason[]>
> = {
  SUB_DOESNT_WORK: [
    "WANT_TO_TRY_FIRST_BOX",
    "PREFER_TO_BUY_WHEN_I_NEED_IT",
    "NEED_LESS_FOOD_THAN_SUBSCRIPTION",
    "UNAWARE_OF_SUB",
    "BUYING_AT_RETAIL",
  ],
  NEW_PACKAGING: [ "PACKAGING_BOX", "PACKAGING_TRAYS" ],
  TOO_MUCH_KATKIN: [ "HAVE_ENOUGH_FOR_NOW", "COMINGBACK_MULTIFEEDING" ],
  HEALTH_REASONS: [ "VOMITING_OR_LOOSE_STOOLS", "REQUIRE_DIFFERENT_KIND_OF_FOOD" ],
  NOT_EATING: [
    "NOT_ENTHUSIASTIC_ENOUGH",
    "LEFTOVER_FOOD",
    "WONT_EAT_AT_ALL",
    "WENT_OFF_FOOD",
    "LIKED_SOME_RECIPES",
  ],
  UNHAPPY_WITH_SERVICE: [
    "UNHAPPY_SERVICE",
    "UNHAPPY_DPD",
    "UNHAPPY_QUALITY",
    "UNHAPPY_OTHER",
  ],
};

export const getSecondaryReasonsForPrimaryReason = (
  primaryReason?: SubscriptionStatusPrimaryReason,
): SubscriptionStatusSecondaryReason[] | undefined => (
  primaryReason ? CANCELLATION_REASON_TAXONOMY[primaryReason] : undefined
);

export const primaryReasonHasSecondaryOptions = (
  primaryReason?: SubscriptionStatusPrimaryReason,
): boolean => !!getSecondaryReasonsForPrimaryReason(primaryReason)?.length;

/**
 * Pure structural check — does NOT enforce that a secondary reason is
 * required for a given primary; that's a UI/DTO concern for each caller.
 */
export const isKnownCancellationReasonCombination = (
  primaryReason: SubscriptionStatusPrimaryReason,
  secondaryReason?: SubscriptionStatusSecondaryReason | null,
): boolean => {
  if (!secondaryReason) {
    return true;
  }

  return !!CANCELLATION_REASON_TAXONOMY[primaryReason]?.includes(secondaryReason);
};
